import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import {
    handleGetGroupsData,
    getModalSwitch,
    setOrderByDirection,
    setOrderByProperty,
    resetState,
    resetScroll,
} from '@app/store/actions';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import {
    getMenuItemById,
    defaultOrgId,
    countText,
    membersCount,
    vaultsCount,
    applicationsCount,
    infiniteScrollItemsFetchLimit,
    hasOrgPermission,
    genericErrorToast,
    interpolateTranslation,
    groupsCount,
    usersCount
} from '@app/helpers';
import {
    IGroup,
    ITableHead,
    IListPageBarEnabled,
    IActiveFilter,
    FilterEnum,
    IFabMenu,
    IDotMenu,
    OrderType,
    Order,
    TranslationsTypes
} from '@app/models';
import { Link, TableCell } from '@material-ui/core';
import { ManageAccount, ReadDirectory, ManageSystem, Audit } from '@app/store/constants';
import { basicService } from '@app/services/basic.service';
import { IModalData, ModalComponent, FabMenu, PasslyTable } from '@app/components';
import AddEditGroupModalForm from '@app/pages/directoryManager/dialogs/addEditGroup.dialog';
import { FormMode } from '@app/helpers/enums';
import { confirm } from '@app/components/Modal/confirmModal';
import { agentsService, appLibraryService, groupsService } from '@app/services';
import { AxiosResponse } from 'axios';

interface IGroupsTableData {
    checked: boolean;
    checkboxDisabled?: boolean;
    name: string;
    memberCount: number;
    member: TranslationsTypes;
    peopleCount: number;
    groupsCount: number;
    people: TranslationsTypes;
    groups: TranslationsTypes;
    vaultCount: number;
    vault: TranslationsTypes;
    applicationCount: number;
    application: TranslationsTypes;
    lastModified: Date;
    syncSource: string;
    applicationSyncSource: string;
    id: string;
    isSystem: boolean;
}

enum TableHeadEnum {
    name = 'name',
    lastModified = 'lastModified',
    syncSource = 'syncSource',
    applicationSyncSource = 'applicationSyncSource'
}

const GroupsPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Groups);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('name');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IGroupsTableData[]>([]);
    const [noPermsToView, setNoPermsToView] = useState(false);
    const [groupsAgentDetails, setGroupsAgentDetails] = useState(null);
    const [groupAppDetails, setGroupAppDetails] = useState(null);

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const groups = useSelector((state: any) => state.groups.groups);
    const groupsTotalCount = useSelector((state: any) => state.groups.count);
    const groupsMemberCount = useSelector((state: any) => state.groups.groupMemberCount);
    const groupsVaultCount = useSelector((state: any) => state.groups.groupVaultCount);
    const groupsApplicationCount = useSelector((state: any) => state.groups.groupApplicationCount);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: hasOrgPermission([ManageAccount])
    };

    const getComplexCell = (row: any) => {
        return (
            <TableCell>
                <div className='user__name'>
                    <Link data-id={row.id} component='button' onClick={editRow}>
                        {row.name}
                    </Link>
                </div>
                <span className='user__data'>{countText(row.memberCount, translation(row.member))}</span>
                <br />

                {basicService.isDefaultOrgBeta() && basicService.orgFeaturePasswordServerEnabled() && (
                    <Fragment>
                        <span className='user__data'>{countText(row.vaultCount, translation(row.vault))}</span> <br />
                    </Fragment>
                )}
                <span className='user__data'>{countText(row.applicationCount, translation(row.application))}</span>
            </TableCell>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.name,
            label: 'GROUPS_NAME',
            type: 'getComplexCell',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.name,
            getComplexCell: getComplexCell
        },
        {
            id: TableHeadEnum.syncSource,
            label: 'SYNC_SOURCE'
        },
        {
            id: TableHeadEnum.applicationSyncSource,
            label: 'APP_SYNC_SOURCE'
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'GROUPS_LAST_MODIFIED',
            type: 'date'
        }
    ];

    const menu: IFabMenu = {
        name: 'NEW_GROUP',
        actions: [
            {
                name: 'NEW_GROUP',
                method: () => addNewGroup(),
                iconElement: <i className='icon icon-user-groups' />
            }
        ]
    };

    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        setNoPermsToView(!hasOrgPermission([ManageSystem, ReadDirectory, ManageAccount, Audit]));

        if (organizationFilter) {
            agentsService.getAgentDetail().then((response: AxiosResponse) => {
                setGroupsAgentDetails(response.data.value);
            });

            appLibraryService.getOffice365Apps().then((response: AxiosResponse) => {
                setGroupAppDetails(response.data.value);
            });
        }
    }, [organizationFilter]);

    useEffect(() => {
        if (!loadingData) {
            const groupMemberForDisplay = (id: string) => groupsMemberCount.filter((group: IGroup) => group.Id === id);
            const groupVaultCountForDisplay = (id: string) => groupsVaultCount.filter((group: IGroup) => group.Id === id);
            const groupApplicationCountForDisplay = (id: string) => groupsApplicationCount.filter((group: IGroup) => group.Id === id);

            let syncSourceText = (syncSource: string, additionalText: string, items: any, field: any) => {
                if (syncSource && syncSource !== defaultOrgId) {
                    // they prob didn't have permissions to see this so just show
                    // local or AD without agent name
                    if (items === null) {
                        return additionalText;
                    } else {
                        for (const agent of items) {
                            if (agent.Id === syncSource) {
                                return agent[field] + ' (' + additionalText + ')';
                            }
                        }
                    }
                }

                return translation('SYNC_SOURCE_LOCAL');
            };

            if (groups.length && groupsMemberCount.length && groupsVaultCount.length && groupsApplicationCount.length) {
                let newRows = groups.map((group: IGroup) => {
                    let count = 0;
                    let usersInGroupCount = 0;
                    let groupsInGroupCount = 0;
                    let vaultCount = 0;
                    let applicationCount = 0;

                    groupMemberForDisplay(group.Id)[0] !== undefined ? (count = groupMemberForDisplay(group.Id)[0].Count) : (count = 0);
                    groupVaultCountForDisplay(group.Id)[0] !== undefined
                        ? (vaultCount = groupVaultCountForDisplay(group.Id)[0].Count)
                        : (vaultCount = 0);
                    groupApplicationCountForDisplay(group.Id)[0] !== undefined
                        ? (applicationCount = groupApplicationCountForDisplay(group.Id)[0].Count)
                        : (applicationCount = 0);

                    let source: string = syncSourceText(group.SyncSource, translation('SYNC_SOURCE_AD'), groupsAgentDetails, 'AgentName');
                    let applicationSource: string = syncSourceText(
                        group.ApplicationSyncSource,
                        translation('SYNC_SOURCE_AAD'),
                        groupAppDetails,
                        'DisplayName'
                    );

                    const data: IGroupsTableData = {
                        checked: false,
                        checkboxDisabled: group.IsSystem,
                        name: group.Name,
                        memberCount: count,
                        member: membersCount(count),
                        peopleCount: usersInGroupCount,
                        groupsCount: groupsInGroupCount,
                        people: usersCount(usersInGroupCount),
                        groups: groupsCount(groupsInGroupCount),
                        vaultCount: vaultCount,
                        vault: vaultsCount(vaultCount),
                        applicationCount: applicationCount,
                        application: applicationsCount(applicationCount),
                        lastModified: group.LastModified,
                        syncSource: source,
                        applicationSyncSource: applicationSource,
                        id: group.Id,
                        isSystem: group.IsSystem
                    };
                    return data;
                });

                setRows(rows.concat(newRows));
            }
        } else if (groups.length === 0) {
            setRows([]);
        }
    }, [loadingData]);

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------
    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const addNewGroup = () => {
        dispatch(getModalSwitch(true));

        setModalData({
            headerTitle: translation('NEW_GROUP'),
            modalSwitcher: modalSwitchState,
            form: <AddEditGroupModalForm formMode={FormMode.Add} handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        dispatch(handleGetGroupsData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, organizationFilter, isReset, activeFilters));
    };

    const emptyGroupList = () => {
        return !loadingData && rows.length === 0 && hasOrgPermission([ManageSystem, ReadDirectory, ManageAccount, Audit]);
    };

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];
        let unsyncedIds: string[] = [];
        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);

                if (row.syncSource.toLocaleLowerCase() === 'local') {
                    unsyncedIds.push(row.id);
                }
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        if (unsyncedIds.length !== ids.length) {
            deleteGroupsNoSyncAreYouSure(ids, unsyncedIds);
        } else {
            deleteGroupAreYouSure(ids);
        }
    }, [rows]);

    const deleteGroupAreYouSure = (ids: any) => {
        confirm(translation('DELETE_GROUP_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_GROUP_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteGroups(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteGroupsNoSyncAreYouSure = (ids: string[], unsyncedIds: string[]) => {
        if (unsyncedIds.length === 0) {
            confirm(translation('SELECT_MORE_SYNC'), translation('OK'), undefined, translation('DELETE_GROUP_HEADER')).then(
                () => {
                    // do nothing on ok
                },
                () => {
                    // do nothing on cancel
                }
            );
        } else {
            let messageBody: string = `${translation('DELETE_GROUP_BODY')} <br><br> ${interpolateTranslation(translation('PARTIAL_DELETE_MESSAGE'), [
                unsyncedIds.length.toString(),
                ids.length.toString()
            ])}`;
            confirm(messageBody, translation('DELETE'), translation('CANCEL'), translation('DELETE_GROUP_HEADER'), true, {
                isDeleteAction: true
            }).then(
                () => {
                    deleteGroups(unsyncedIds);
                },
                () => {
                    // do nothing on cancel
                }
            );
        }
    };

    const deleteGroups = (ids: string[]) => {
        if (ids.length > 0) {
            let groups: any = {
                Groups: ids
            };

            groupsService.deleteGroups(groups, genericErrorToast, handleDeleteSuccess);
        }
    };

    const detachGroups = (ids: string[]) => {
        if (ids.length > 0) {
            let groups: any = {
                Groups: ids
            };

            groupsService.detachGroups(groups, genericErrorToast, handleDetachSuccess);
        }
    };

    const detachSelected = useCallback(() => {
        let ids: string[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        confirm(translation('DETACH_GROUP_BODY'), translation('DETACH'), translation('CANCEL'), translation('DETACH_GROUP_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                detachGroups(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    }, [rows]);

    const handleDeleteSuccess = (response: any) => {
        let results: string = response.data.value;

        if (results !== undefined && results !== '') {
            results = results.toString().split('|').join('<br/>');

            confirm(results, translation('OK'), undefined, undefined, true).then(
                () => {
                    handlePageRefreshFromDialog(true);
                },
                () => {
                    // do nothing on cancel
                }
            );
        } else {
            handlePageRefreshFromDialog(true);
        }
    };

    const handleDetachSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DETACH_SELECTED'),
            action: detachSelected
        },
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const editRow = (e: any) => {
        basicService.redirect('/directory/groups/details/' + e.currentTarget.dataset.id);
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deleteGroupAreYouSure(ids);
    };

    return (
        <>
            <ModalComponent {...modalData} />

            {noPermsToView ? (
                <div className='table--empty'>{translation('NO_ORG_GROUP_PERMISSIONS')}</div>
            ) : (
                <>
                    <PasslyTable
                        rows={rows}
                        setRows={setRows}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={groupsTotalCount}
                        onFetch={fetchMoreData}
                        withCheckboxes={hasOrgPermission([ManageAccount])}
                        actionMenuItems={actionMenuItems}
                        emptyListHelper={translation('EMPTY_GROUP_LIST')}
                        loadingData={loadingData}
                        editRow={editRow}
                        deleteRow={deleteRow}
                    />

                    {hasOrgPermission([ManageAccount]) && (
                        <FabMenu
                            menu={menu}
                            hideTooltip={!emptyGroupList()}
                            tooltipTitle={translation('GROUPS_NO_GROUPS_TOOL_TIP_TITLE')}
                            tooltipDescription={translation('GROUPS_NO_GROUPS_TOOL_TIP_DESC')}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default GroupsPage;
